import React, { useEffect, useState } from 'react';
import { PageContent } from '@/shared/widgets/layout/PageContent';
import { Button } from '@/shared/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { Badge } from '@/shared/ui/badge';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { Save, RotateCcw, RefreshCw, AlertCircle, Plus } from 'lucide-react';
import { HighlightItemList } from '../components/HighlightItemList';
import { useHighlightManagement } from '../hooks/useHighlightManagement';
import { HIGHLIGHT_SECTIONS } from '../types/highlight.types';
import { ReactSelect, SelectOption } from '@/shared/widgets/react-select';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/shared/widgets/toast';
import { HighlightService } from '../services/highlightService';
import { FormFooter } from '@/shared/widgets/form-actions';

export default function HighlightManagementPage() {
  const [selectedSection, setSelectedSection] = useState<string>('');
  const [selectedItemsToAdd, setSelectedItemsToAdd] = useState<string[]>([]);
  const [search, setSearch] = useState<string>('');
  const queryClient = useQueryClient();

  const {
    items,
    isLoading,
    isError,
    hasChanges,
    isSaving,
    isRemoving,
    reorderItems,
    saveChanges,
    resetChanges,
    removeItems,
    refetch,
    refreshItems,
  } = useHighlightManagement(selectedSection);

  // Query para buscar itens disponíveis
  const {
    data: availableItems,
    isLoading: isLoadingAvailable,
    refetch: refetchAvailableItems,
  } = useQuery({
    queryKey: ['available-items', selectedSection],
    queryFn: () => HighlightService.getAvailableItems(selectedSection, search),
    enabled: !!selectedSection,
  });

  // Mutation para adicionar itens
  const addItemsMutation = useMutation({
    mutationFn: (itemIds: string[]) => {
      const lastOrder = items.filter(item => item.order !== null).sort((a, b) => a.order - b.order)[items.length - 1].order;
      const newItemsWithOrder = itemIds.map(id => ({ id, order: lastOrder + 1 }));
      return HighlightService.addItemsToSection(selectedSection, newItemsWithOrder);
    }
    ,
    onSuccess: () => {
      toast.success(
        `${selectedItemsToAdd.length} item(s) adicionado(s) com sucesso!`,
        `Os itens foram adicionados à seção ${selectedSectionData?.name}.`
      );
      setSelectedItemsToAdd([]);
      refreshItems();
      queryClient.invalidateQueries({
        queryKey: ['available-items', selectedSection],
      });
    },
    onError: (error: any) => {
      toast.api.error(
        'Erro ao adicionar itens',
        error.message || 'Ocorreu um erro inesperado ao adicionar os itens.'
      );
    },
  });

  const selectedSectionData = HIGHLIGHT_SECTIONS.find(
    (section) => section.key === selectedSection
  );

  const handleSectionChange = (value: string) => {
    setSelectedSection(value);
  };

  const handleSave = () => {
    saveChanges();
  };

  const handleReset = () => {
    resetChanges();
  };

  const handleRefresh = () => {
    refetch();
  };

  const handleAddItems = () => {
    if (selectedItemsToAdd.length === 0) {
      toast.warning('Nenhum item selecionado', 'Selecione pelo menos um item para adicionar à seção.');
      return;
    }
    addItemsMutation.mutate(selectedItemsToAdd);
  };

  const handleRemoveItem = (itemId: string) => {
    removeItems([itemId]);
  };

  const availableOptions: SelectOption[] =
    availableItems?.map((item) => ({
      value: item.id,
      label: item.name,
      description: item.creatorName,
      icon: item.cover ? (
        <img src={item.cover} alt={item.name} className="w-6 h-6 rounded object-cover" />
      ) : undefined,
    })) || [];

  useEffect(() => {
    refetchAvailableItems();
  }, [search, refetchAvailableItems]);

  return (
    <PageContent
      title="Gerenciar Ordenação das Seções"
      items={[
        {
          label: "Início",
          href: "/",
        },
        {
          label: "Gerenciar Seções",
          href: "/admin/highlights",
        },
      ]}
    >
      <div className="space-y-6">
        {/* Seletor de Seção */}
        <Card className="bg-[#222222] border-white/10">
          <CardHeader>
            <CardTitle className="text-white">Selecionar Seção</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <Select value={selectedSection} onValueChange={handleSectionChange}>
                <SelectTrigger className="w-[300px] bg-[#333333] border-white/10 text-white">
                  <SelectValue placeholder="Escolha uma seção para gerenciar" />
                </SelectTrigger>
                <SelectContent className="bg-[#333333] border-white/10">
                  {HIGHLIGHT_SECTIONS.map((section) => (
                    <SelectItem key={section.key} value={section.key} className="text-white hover:bg-white/10">
                      {section.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {selectedSectionData && (
                <Badge variant="secondary" className="bg-[#D91657] text-white">
                  {selectedSectionData.name}
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Ações */}
        {selectedSection && (
          <Card className="bg-[#222222] border-white/10">
            <CardHeader>
              <CardTitle className="flex items-center justify-between text-white">
                <span>Gerenciar Ordenação</span>
                <div className="flex items-center gap-2">
                  {hasChanges && (
                    <Badge variant="outline" className="text-orange-400 border-orange-400">
                      Alterações não salvas
                    </Badge>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRefresh}
                    disabled={isLoading || isSaving}
                    className="bg-[#333333] border-white/10 text-white hover:bg-white/10"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Atualizar
                  </Button>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">

                {/* Seletor de itens para adicionar */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-300">
                    Adicionar Novos Itens:
                  </label>
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <ReactSelect
                        options={availableOptions}
                        value={selectedItemsToAdd}
                        onChange={(value) => setSelectedItemsToAdd(Array.isArray(value) ? value : [])}
                        placeholder="Selecione itens para adicionar..."
                        multiple={true}
                        searchable={true}
                        clearable={true}
                        loading={isLoadingAvailable}
                        noOptionsMessage="Nenhum item disponível"
                        className="min-w-[400px]"
                        onSearch={setSearch}
                        filterOption={() => true}
                      />
                    </div>
                    <Button
                      onClick={handleAddItems}
                      disabled={selectedItemsToAdd.length === 0 || addItemsMutation.isPending}
                      className="bg-[#D91657] hover:bg-[#C1174F] text-white"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      {addItemsMutation.isPending ? 'Adicionando...' : `Adicionar ${selectedItemsToAdd.length}`}
                    </Button>
                  </div>
                </div>

                {/* Instruções */}
                <Alert className="bg-[#333333] border-white/10">
                  <AlertCircle className="h-4 w-4 text-[#D91657]" />
                  <AlertDescription className="text-white/70">
                    Arraste e solte os itens para reordená-los. As alterações serão salvas apenas quando você clicar em "Salvar Alterações".
                  </AlertDescription>
                </Alert>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Lista de Itens */}
        {selectedSection && (
          <Card className="bg-[#222222] border-white/10">
            <CardHeader>
              <CardTitle className="text-white">
                Itens da Seção: {selectedSectionData?.name}
                {items.length > 0 && (
                  <Badge variant="secondary" className="ml-2 bg-[#D91657] text-white">
                    {items.length} item{items.length !== 1 ? 's' : ''}
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isError ? (
                <Alert variant="destructive" className="bg-red-900/20 border-red-500/20">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription className="text-red-200">
                    Erro ao carregar os itens da seção. Tente novamente.
                  </AlertDescription>
                </Alert>
              ) : (
                <HighlightItemList
                  items={items}
                  onReorder={reorderItems}
                  onRemove={handleRemoveItem}
                  isLoading={isLoading}
                  isRemoving={isRemoving}
                />
              )}
            </CardContent>
          </Card>
        )}

        {/* Mensagem quando nenhuma seção está selecionada */}
        {!selectedSection && (
          <Card className="bg-[#222222] border-white/10">
            <CardContent className="text-center py-12">
              <div className="text-white/40 mb-4">
                <AlertCircle className="h-12 w-12 mx-auto" />
              </div>
              <h3 className="text-lg font-medium text-white mb-2">
                Selecione uma seção
              </h3>
              <p className="text-white/60">
                Escolha uma seção acima para começar a gerenciar a ordenação dos itens.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Footer com botões de ação */}
        {selectedSection && (
          <FormFooter className="mt-8">
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={handleReset}
                disabled={!hasChanges || isSaving}
                className="bg-transparent border-[#333333] text-white hover:bg-[#222222] hover:text-white/60"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Descartar Alterações
              </Button>
              <Button
                onClick={handleSave}
                disabled={!hasChanges || isSaving}
                className="bg-[#D91657] hover:bg-[#C1174F] text-white"
              >
                <Save className="h-4 w-4 mr-2" />
                {isSaving ? 'Salvando...' : 'Salvar Alterações'}
              </Button>
            </div>
          </FormFooter>
        )}
      </div>
    </PageContent>
  );
}
